import { getConsentStatus, isDntEnabled } from './consent';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PremiumFeatureContext {
  isPaidPro: boolean;
  tempUnlocked: boolean;
  betaUnlocked?: boolean;
  metricId?: string;
}

// ---------------------------------------------------------------------------
// Dedup guards
// ---------------------------------------------------------------------------

const firedOnce = new Set<string>();

function trackOnce(
  key: string,
  event: string,
  properties: Record<string, unknown>
) {
  if (firedOnce.has(key)) return;
  firedOnce.add(key);
  track(event, properties);
}

function trackOncePerUser(
  storageKey: string,
  event: string,
  properties: Record<string, unknown>
) {
  if (localStorage.getItem(storageKey)) return;
  localStorage.setItem(storageKey, 'true');
  track(event, properties);
}

// ---------------------------------------------------------------------------
// GA4 loader
// ---------------------------------------------------------------------------

let gtagLoaded = false;

function loadGtag(measurementId: string): void {
  if (gtagLoaded) return;
  gtagLoaded = true;

  // Initialise dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };

  // Consent Mode v2 defaults — denied before user choice
  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  });

  // If consent was previously granted, update immediately
  if (getConsentStatus() === 'granted') {
    window.gtag('consent', 'update', { analytics_storage: 'granted' });
  }

  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    send_page_view: false, // We handle page views via React Router
    debug_mode: import.meta.env.DEV,
  });

  // Inject the gtag.js script tag
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);
}

// ---------------------------------------------------------------------------
// Initialisation — call once at app startup (before React renders)
// ---------------------------------------------------------------------------

export function initAnalytics(): void {
  if (typeof window === 'undefined') return;

  // DNT overrides everything — skip gtag entirely
  if (isDntEnabled()) return;

  const measurementId = import.meta.env.VITE_GA4_MEASUREMENT_ID;
  if (!measurementId) return;

  loadGtag(measurementId);

  // Listen for consent changes (from banner or Cookie Preferences link)
  window.addEventListener('plevits:consent-change', ((
    e: CustomEvent<string>
  ) => {
    if (!window.gtag) return;
    if (e.detail === 'granted') {
      window.gtag('consent', 'update', { analytics_storage: 'granted' });
    } else {
      window.gtag('consent', 'update', { analytics_storage: 'denied' });
    }
  }) as EventListener);
}

// ---------------------------------------------------------------------------
// Core track function
// ---------------------------------------------------------------------------

function track(event: string, properties: Record<string, unknown>) {
  if (typeof window === 'undefined') return;

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log(`[analytics] ${event}`, properties);
  }

  // Only send to GA4 if gtag is loaded and consent is granted
  if (
    typeof window.gtag === 'function' &&
    getConsentStatus() === 'granted' &&
    !isDntEnabled()
  ) {
    try {
      window.gtag('event', event, properties);
    } catch {
      // Ad-blocker may have removed gtag mid-session — fail silently
    }
  }
}

// ---------------------------------------------------------------------------
// GA4 user properties
// ---------------------------------------------------------------------------

export function setUserProperties(
  properties: Record<string, unknown>
): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function')
    return;
  if (getConsentStatus() !== 'granted' || isDntEnabled()) return;
  try {
    window.gtag('set', 'user_properties', properties);
  } catch {
    // fail silently
  }
}

export function setUserId(
  measurementId: string,
  userId: string
): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function')
    return;
  if (getConsentStatus() !== 'granted' || isDntEnabled()) return;
  try {
    window.gtag('config', measurementId, { user_id: userId });
  } catch {
    // fail silently
  }
}

export function clearUserId(measurementId: string): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function')
    return;
  try {
    window.gtag('config', measurementId, { user_id: null });
  } catch {
    // fail silently
  }
}

// ---------------------------------------------------------------------------
// Page view tracking
// ---------------------------------------------------------------------------

export function trackPageView(path: string, title?: string): void {
  track('page_view', {
    page_path: path,
    ...(title && { page_title: title }),
  });
}

// ---------------------------------------------------------------------------
// Existing event tracking functions (signatures unchanged)
// ---------------------------------------------------------------------------

export function trackPremiumFeatureView(
  feature: string,
  context: PremiumFeatureContext
) {
  track('premium_feature_view', {
    feature,
    ...context,
    event_version: 'v1',
  });
}

export function trackUpgradePromptShown(
  feature: string,
  context: PremiumFeatureContext
) {
  track('upgrade_prompt_shown', {
    feature,
    ...context,
    event_version: 'v1',
  });
}

export function trackUpgradeBannerClicked(context: PremiumFeatureContext) {
  track('upgrade_banner_clicked', { ...context, event_version: 'v1' });
}

export function trackUpgradeCtaClicked(
  section: string,
  context: PremiumFeatureContext
) {
  track('upgrade_cta_clicked', { section, ...context, event_version: 'v1' });
}

export function trackStripeCheckoutStarted(
  feature: string,
  context: PremiumFeatureContext
) {
  track('stripe_checkout_started', {
    feature,
    ...context,
    event_version: 'v1',
  });
}

export function trackContactSalesFormSubmitted(
  teamSize: string,
  source?: string
) {
  track('contact_sales_form_submitted', {
    teamSize,
    source,
    event_version: 'v1',
  });
}

// ---------------------------------------------------------------------------
// New conversion funnel events
// ---------------------------------------------------------------------------

export function trackSignUpStarted(): void {
  trackOnce('sign_up_started', 'sign_up_started', { event_version: 'v1' });
}

/** GA4 recommended event name: sign_up */
export function trackSignUp(): void {
  track('sign_up', {});
}

export function trackEmailVerified(): void {
  track('email_verified', { event_version: 'v1' });
}

/** GA4 recommended event name: login */
export function trackLogin(): void {
  track('login', {});
}

export function trackWorkspaceCreated(): void {
  track('workspace_created', { event_version: 'v1' });
}

export function trackStripeConnected(): void {
  track('stripe_connected', { event_version: 'v1' });
}

export function trackFirstDashboardView(): void {
  trackOncePerUser(
    'plevits_first_dashboard_seen',
    'first_dashboard_view',
    { event_version: 'v1' }
  );
}

// ---------------------------------------------------------------------------
// Feature adoption events
// ---------------------------------------------------------------------------

export function trackPaywallHit(featureId: string): void {
  track('paywall_hit', { feature_id: featureId, event_version: 'v1' });
}

export function trackFeatureUsed(
  featureId: string,
  category: string
): void {
  track('feature_used', {
    feature_id: featureId,
    category,
    event_version: 'v1',
  });
}

// ---------------------------------------------------------------------------
// Revenue events
// ---------------------------------------------------------------------------

/** GA4 recommended event name: purchase. Skip if transactionId is missing. */
export function trackPurchase(
  transactionId: string,
  value: number,
  currency: string,
  itemName?: string
): void {
  if (!transactionId) return;

  // Idempotency: prevent duplicate fires on refresh/back-navigation
  const sentKey = `plevits_purchase_sent_${transactionId}`;
  if (sessionStorage.getItem(sentKey)) return;
  sessionStorage.setItem(sentKey, 'true');

  track('purchase', {
    transaction_id: transactionId,
    value,
    currency,
    items: [
      {
        item_name: itemName || 'Pro Subscription',
        quantity: 1,
        price: value,
      },
    ],
  });
}

export function trackSubscriptionCancelled(): void {
  track('subscription_cancelled', { event_version: 'v1' });
}

export function trackBillingPortalOpened(): void {
  track('billing_portal_opened', { event_version: 'v1' });
}

// ---------------------------------------------------------------------------
// Engagement events
// ---------------------------------------------------------------------------

export function trackWorkspaceSwitched(): void {
  track('workspace_switched', { event_version: 'v1' });
}

export function trackSettingsUpdated(section: string): void {
  track('settings_updated', { section, event_version: 'v1' });
}

export function trackPublicProfileEnabled(): void {
  track('public_profile_enabled', { event_version: 'v1' });
}

export function trackShareProfile(method: string): void {
  track('share_profile', { method, event_version: 'v1' });
}

export function trackDirectoryBrowsed(): void {
  trackOnce('directory_browsed', 'directory_browsed', {
    event_version: 'v1',
  });
}

export function trackContentPageViewed(slug: string): void {
  trackOnce(
    `content_page_viewed:${slug}`,
    'content_page_viewed',
    { content_slug: slug, event_version: 'v1' }
  );
}
